import { useParams } from 'react-router-dom';
import { Info, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { QueryTag } from '@common/ts/enums';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { WarningMessage } from '@common/components';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Button } from '../ui/button';
import CompleteProjectTable from '../completeProjectTable/CompleteProjectTable';
import { useMutateCompleteTellet, useMutateReRunAnalyze } from '@/reactQuery/post';
import useTranslation from '@/hooks/useTranslation';
import { ECompanyThemeName } from '@/enums';

interface CompleteProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function CompleteProjectModal({ isOpen, onClose }: CompleteProjectModalProps) {
  const { project_id, organization_id, workspace_id } = useParams();
  const { mutateAsync: mutateAsyncCompleteTellet } = useMutateCompleteTellet(
    organization_id,
    workspace_id,
    project_id,
  );
  const { lang } = useTranslation();
  const { themeName } = useTheme();
  const queryClient = useQueryClient();

  const { mutateAsync: mutateAsyncReRun, isPending: isPendingReRun } = useMutateReRunAnalyze();

  // TELV1-268 / D1 — complete FIRST, then run. The run gate's
  // completion guarantee keys off the server-owned COMPLETED status
  // (unforgeable); running before completing would judge the run
  // under the live-project milestone and could refuse it. It also
  // matches intent: the user asked to complete; the wrap-up analysis
  // is a consequence, so its failure must not block completion.
  const onTelletComplete = async () => {
    try {
      await mutateAsyncCompleteTellet({});
    } catch {
      toast.error(lang.get('msg.errorPleaseTryAgain'));
      return;
    }
    await queryClient.invalidateQueries({ queryKey: [QueryTag.INTERVIEW_QUESTION_CATEGORIES] });
    toast.success(lang.get('msg.projectSuccessfullyCompleted'));
    onClose();
    try {
      const res = await mutateAsyncReRun({ project_id });
      // Store the run state so ProjectResults can track analysis progress.
      // A zero-new completion returns DONE with no pipeline started —
      // storing that state_id is harmless (first poll clears it).
      if (res.data?.state_id) {
        localStorage.setItem(`rerun_state_${project_id}`, res.data.state_id);
      }
    } catch {
      // The project IS completed; only the wrap-up analysis failed.
      toast.error(lang.get('msg.completionAnalysisFailedRetry'));
    }
  };

  return (
    <AlertDialog open={isOpen}>
      <div>
        {isOpen && (
          <AlertDialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <div>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex flex-wrap items-center gap-2">
                  <p>
                    {lang.get('msg.complete')}{' '}
                    {themeName === ECompanyThemeName.EMPATHY ? 'project' : 'Tellet'}
                  </p>
                </AlertDialogTitle>
                <p className="text-sm text-dark-text">
                  {themeName === ECompanyThemeName.EMPATHY
                    ? lang.get('msg.areYouSureYouWantToCompleteThisTellet_empathy')
                    : lang.get('msg.areYouSureYouWantToCompleteThisTellet')}
                </p>
              </AlertDialogHeader>

              <WarningMessage>
                <div>
                  <span>{lang.get('msg.note')}:</span>
                  <ul className="list-disc pl-5 mt-1">
                    <li>{lang.get('msg.conversationDeletionWarning')}</li>
                    <li>{lang.get('msg.reCategorizationWarning')}</li>
                  </ul>
                </div>
              </WarningMessage>

              {/* project data */}
              <div className="flex flex-col w-full">
                <CompleteProjectTable />

                {isPendingReRun && (
                  <div className="flex gap-2 mb-8 -mt-5 text-sm">
                    <Info size={27} className="pt-3 text-red-600" />
                    <p>{lang.get('msg.completeTelletInfoText')}</p>
                  </div>
                )}
              </div>

              <AlertDialogFooter>
                {!isPendingReRun && (
                  <AlertDialogCancel onClick={onClose}>{lang.get('msg.cancel')}</AlertDialogCancel>
                )}

                <Button variant="default" disabled={isPendingReRun} onClick={onTelletComplete}>
                  {isPendingReRun ? (
                    <span className="flex items-center gap-1.5">
                      <Loader2 className="animate-spin" size={20} />
                      {lang.get('msg.completingAndRunningNewChanges')}
                      ...
                    </span>
                  ) : (
                    lang.get('msg.complete')
                  )}
                </Button>
              </AlertDialogFooter>
            </div>
          </AlertDialogContent>
        )}
      </div>
    </AlertDialog>
  );
}

export default CompleteProjectModal;
